import SearchableSelect from "@/lib/shadcn/components/searchable-select";
import React, { useEffect, useMemo } from "react";
import { useAppData } from "@/hooks/useAppData";
import { useUserSettingsDispatch } from "@/store/slices/userSettingsDispatch";
import { SelectOption } from "@/lib/molecules/select";
import { Card, CardContent, CardHeader } from "@/lib/shadcn/ui/card";
import { PeriodName } from "@/models";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/lib/shadcn/ui/alert";
import { overViewAdrFormatter, overViewIncomeFormatter } from "@/utils/formatters";
import { ChartConfig } from "@/lib/shadcn/ui/chart";
import { DonutChartData, DonutChartWithText } from "@/lib/shadcn/components/donut-chart-with-text";
import { SinglePointMultiBarChartData, SinglePontMultiBarChart } from "@/lib/shadcn/components/single-point-multi-bar-chart";
import { Apartment, BltStatsItem } from "@/models/responses";
import { useActiveApartments, useActiveApartmentEntities } from "@/store/slices/apartments";
import { useGetOwnerBltStatsQuery } from "@/store/booking-management-api";

interface BltStats {
    grossIncome: number;
    netIncome: number;
    bookingsReceived: number;
    daysBooked: number;
    netAdr: number;
    grossAdr: number;
    leadDaysGroup: string;
}

/**
 * Owner-scoped fork of the admin BLT (Booking Lead Time) breakdown page.
 *
 * Differences from the admin `blt.tsx`:
 *  - NO apartment-group dimension: neither the admin group select nor the
 *    shared `<ApartmentFilterBar />` is rendered.
 *  - An Owner selector renders ONLY when the user can see more than one owner
 *    (`ownerOptions.length > 1`); a single-owner user is pinned to their one
 *    owner and sees no owner selector.
 *  - The apartment selector is narrowed to the selected owner's apartments.
 *  - Data comes from `useGetOwnerBltStatsQuery` (`/api/owners/:ownerId/blt`),
 *    fired twice: once for the selected year and once for the previous year
 *    (the year-over-year comparison the donut cards render).
 *
 * The BLT query is skipped until an owner id resolves; a multi-owner user
 * defaults `selectedOwner` to the first (alphabetically-sorted) owner on mount
 * so the page is never stuck behind the loading gate.
 */
function constructStats(bltStats: BltStatsItem[], apartmentId: number, entities: Record<string, Apartment>): Map<string, BltStats> {
    const stats = new Map<string, BltStats>();
    bltStats
        .filter((s) => {
            if (apartmentId === 0) {
                return true;
            }
            const apartment = entities[s.apartmentId];
            return apartmentId === apartment?.id;
        })
        .forEach((s) => {
            if (!stats.has(s.leadDaysGroup)) {
                stats.set(s.leadDaysGroup, {
                    grossIncome: 0,
                    netIncome: 0,
                    bookingsReceived: 0,
                    daysBooked: 0,
                    netAdr: 0,
                    grossAdr: 0,
                    leadDaysGroup: s.leadDaysGroup,
                });
            }
            const stat = stats.get(s.leadDaysGroup);
            if (stat !== undefined) {
                stat.grossIncome += s.grossIncome;
                stat.netIncome += s.netIncome;
                stat.bookingsReceived += s.bookingsReceived;
                stat.daysBooked += s.daysBooked;
                if (s.daysBooked > 0) {
                    stat.netAdr = stat.netIncome / stat.daysBooked;
                    stat.grossAdr = stat.grossIncome / stat.daysBooked;
                }
            }
        });
    return stats;
}

const normalizeGroupKey = (leadDaysGroup: string): string => leadDaysGroup.replace(" ", "").replace("-", "").replace("+", "");

const OwnerBltPage: React.FC = () => {
    const { setSelectedYear, setSelectedPeriod, setSelectedOwner, setSelectedApartment } = useUserSettingsDispatch();
    const { ownerOptions, selectedOwner, selectedApartment, selectedPeriod, selectedYear, periodOptions, yearOptions } = useAppData();

    const apartmentEntities = useActiveApartmentEntities();
    const activeApartments = useActiveApartments();

    // Effective owner id: the persisted selection when set, otherwise the first
    // (alphabetical) owner so the BLT query can fire before the userSettings
    // matcher settles `selectedOwner` (which starts at 0).
    const effectiveOwnerId = selectedOwner !== 0 ? selectedOwner : ownerOptions.length > 0 ? Number(ownerOptions[0].value) : 0;

    // Persist the first-owner default so downstream reads (and localStorage) agree.
    useEffect(() => {
        if (selectedOwner === 0 && ownerOptions.length > 0) {
            setSelectedOwner(Number(ownerOptions[0].value));
        }
    }, [ownerOptions, selectedOwner, setSelectedOwner]);

    // The apartments visible to the selected owner (owner-scoped selector).
    const ownerApartments = useMemo(
        () => activeApartments.filter((a) => a.ownerIds.includes(effectiveOwnerId)),
        [activeApartments, effectiveOwnerId],
    );

    const apartmentOptions: SelectOption[] = useMemo(
        () => [{ label: "All", value: "0" }, ...ownerApartments.map((a) => ({ label: a.name, value: a.id.toString() }))],
        [ownerApartments],
    );

    // When the owner changes, any apartment selection that falls outside the new
    // owner's scope is reset to the aggregate ("All").
    useEffect(() => {
        if (effectiveOwnerId !== 0 && selectedApartment !== 0 && !ownerApartments.some((a) => a.id === selectedApartment)) {
            setSelectedApartment(0);
        }
    }, [effectiveOwnerId, ownerApartments, selectedApartment, setSelectedApartment]);

    const {
        data: bltStats,
        isLoading,
        error,
    } = useGetOwnerBltStatsQuery(
        { ownerId: effectiveOwnerId, period: selectedPeriod, year: selectedYear.toString() },
        { skip: effectiveOwnerId === 0 },
    );

    const { data: bltStatsPrevious } = useGetOwnerBltStatsQuery(
        { ownerId: effectiveOwnerId, period: selectedPeriod, year: (selectedYear - 1).toString() },
        { skip: effectiveOwnerId === 0 },
    );

    const filteredStats = useMemo(() => {
        if (!bltStats) {
            return new Map<string, BltStats>();
        }
        return constructStats(bltStats, selectedApartment, apartmentEntities);
    }, [bltStats, selectedApartment, apartmentEntities]);

    const filteredStatsPrevious = useMemo(() => {
        if (!bltStatsPrevious) {
            return new Map<string, BltStats>();
        }
        return constructStats(bltStatsPrevious, selectedApartment, apartmentEntities);
    }, [bltStatsPrevious, selectedApartment, apartmentEntities]);

    const donutData = (stats: Map<string, BltStats>, pick: (s: BltStats) => number): DonutChartData[] => {
        const ret: DonutChartData[] = [];
        stats.forEach((stat) => {
            const value = pick(stat);
            if (value !== 0) {
                ret.push({
                    group: normalizeGroupKey(stat.leadDaysGroup),
                    value,
                    fill: `var(--color-${normalizeGroupKey(stat.leadDaysGroup)}`,
                });
            }
        });
        return ret;
    };

    const barData = (stats: Map<string, BltStats>, pick: (s: BltStats) => number): SinglePointMultiBarChartData => {
        const ret: SinglePointMultiBarChartData = {};
        stats.forEach((stat) => {
            const value = pick(stat);
            if (value !== 0) {
                ret[normalizeGroupKey(stat.leadDaysGroup)] = value;
            }
        });
        return ret;
    };

    const grossIncomeData = useMemo(() => donutData(filteredStats, (s) => s.grossIncome), [filteredStats]);
    const grossIncomePreviousData = useMemo(() => donutData(filteredStatsPrevious, (s) => s.grossIncome), [filteredStatsPrevious]);
    const daysBookedData = useMemo(() => donutData(filteredStats, (s) => s.daysBooked), [filteredStats]);
    const daysBookedPreviousData = useMemo(() => donutData(filteredStatsPrevious, (s) => s.daysBooked), [filteredStatsPrevious]);
    const netIncomeData = useMemo(() => donutData(filteredStats, (s) => s.netIncome), [filteredStats]);
    const netIncomePreviousData = useMemo(() => donutData(filteredStatsPrevious, (s) => s.netIncome), [filteredStatsPrevious]);
    const grossAdrData = useMemo(() => barData(filteredStats, (s) => s.grossAdr), [filteredStats]);
    const grossAdrPreviousData = useMemo(() => barData(filteredStatsPrevious, (s) => s.grossAdr), [filteredStatsPrevious]);
    const netAdrData = useMemo(() => barData(filteredStats, (s) => s.netAdr), [filteredStats]);
    const netAdrPreviousData = useMemo(() => barData(filteredStatsPrevious, (s) => s.netAdr), [filteredStatsPrevious]);

    const chartConfig = useMemo(() => {
        const config: ChartConfig = {
            grossIncome: { label: "Gross Income" },
            netIncome: { label: "Net Income" },
            bookingsReceived: { label: "Bookings Received" },
            daysBooked: { label: "Days Booked" },
            netAdr: { label: "Net ADR" },
            grossAdr: { label: "Gross ADR" },
        };
        let index = 0;
        filteredStats.forEach((stat) => {
            config[normalizeGroupKey(stat.leadDaysGroup)] = {
                label: stat.leadDaysGroup,
                color: "hsl(var(--chart-" + ++index + "))",
            };
        });
        return config;
    }, [filteredStats]);

    const showLoader = effectiveOwnerId === 0 || isLoading;

    useEffect(() => {
        document.title = "Booking Lead Time Breakdown";
    }, []);

    return (
        <div data-testid="owner-blt-page">
            <h2 className={"font-semibold text-xl"}>Booking Lead Time Breakdown</h2>
            <div role="group" aria-label="Filters" className="flex flex-col lg:flex-row lg:justify-end gap-2 mt-6">
                {ownerOptions.length > 1 && (
                    <div data-testid="owner-select">
                        <SearchableSelect
                            variant="volt"
                            onChange={(value) => setSelectedOwner(Number(value))}
                            placeholder="Select Owner"
                            options={ownerOptions}
                            selectedValue={effectiveOwnerId.toString()}
                            width="w-auto"
                        />
                    </div>
                )}
                <div data-testid="owner-apartment-select">
                    <SearchableSelect
                        variant="volt"
                        selectedValue={selectedApartment.toString()}
                        onChange={(value) => setSelectedApartment(Number(value))}
                        placeholder="Select Apartment"
                        options={apartmentOptions}
                        selectedValuePrefix="Apartment"
                        width="w-auto"
                    />
                </div>
                <SearchableSelect
                    variant="volt"
                    onChange={(val) => setSelectedPeriod(val as PeriodName)}
                    placeholder="Select Period"
                    options={periodOptions || []}
                    selectedValue={selectedPeriod}
                    width="w-auto"
                />
                <SearchableSelect
                    variant="volt"
                    onChange={(year) => setSelectedYear(Number(year))}
                    placeholder="Select Year"
                    options={yearOptions || []}
                    selectedValue={selectedYear.toString()}
                    width="w-auto"
                />
            </div>
            {showLoader ? (
                <div role="status" aria-label="Loading booking lead time data" className="flex justify-center items-center min-h-[200px]">
                    <Loader2 className="max-h-8 max-w-8 animate-spin" />
                    <span className="sr-only">Loading…</span>
                </div>
            ) : error ? (
                <Alert variant="destructive">
                    <AlertDescription>{error.toString()}</AlertDescription>
                </Alert>
            ) : (
                <>
                    {ownerApartments.length === 0 ? (
                        <Alert data-testid="owner-blt-empty-state" className="mt-6">
                            <AlertDescription>No apartments are available for the selected owner.</AlertDescription>
                        </Alert>
                    ) : (
                        <Card className="shadow-sm mt-6">
                            <CardHeader className="overflow-visible">
                                <CardContent className="p-6">
                                    <div className="grid grid-cols-3 gap-4 w-full">
                                        <div data-testid="blt-gross-income-total" className="min-w-0">
                                            <DonutChartWithText
                                                data={grossIncomeData}
                                                dataPrevious={grossIncomePreviousData}
                                                config={chartConfig}
                                                metricLabel={"Gross Income"}
                                                title={"Gross Income Breakdown"}
                                                description={`Gross Income grouped by lead days`}
                                                valueFormatter={overViewIncomeFormatter}
                                            />
                                        </div>

                                        <div className="min-w-0">
                                            <DonutChartWithText
                                                data={daysBookedData}
                                                dataPrevious={daysBookedPreviousData}
                                                config={chartConfig}
                                                metricLabel={"Days Booked"}
                                                title={"Days Booked Breakdown"}
                                                description={`Days Booked grouped by lead days`}
                                            />
                                        </div>

                                        <div className="min-w-0">
                                            <DonutChartWithText
                                                data={netIncomeData}
                                                dataPrevious={netIncomePreviousData}
                                                config={chartConfig}
                                                metricLabel={"Net Income"}
                                                title={"Net Income Breakdown"}
                                                description={`Net Income grouped by lead days`}
                                                valueFormatter={overViewIncomeFormatter}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex justify-between space-x-4 w-full mt-6">
                                        <SinglePontMultiBarChart
                                            description={`Gross ADR grouped by lead days`}
                                            title={"Gross Adr"}
                                            data={grossAdrData}
                                            dataPrevious={grossAdrPreviousData}
                                            config={chartConfig}
                                            tickLabel={`${selectedPeriod} ${selectedYear}`}
                                            barValueFormatter={overViewAdrFormatter}
                                        />
                                        <SinglePontMultiBarChart
                                            description={`Net ADR grouped by lead days`}
                                            title={"Net Adr"}
                                            data={netAdrData}
                                            dataPrevious={netAdrPreviousData}
                                            config={chartConfig}
                                            tickLabel={`${selectedPeriod} ${selectedYear}`}
                                            barValueFormatter={overViewAdrFormatter}
                                        />
                                    </div>
                                </CardContent>
                            </CardHeader>
                        </Card>
                    )}
                </>
            )}
        </div>
    );
};

export default OwnerBltPage;
